import React, { createContext, useContext, useState, useEffect } from 'react';
import { ApiResponse, User, RoleType } from '../types/api';
import { AuthSession, RegisterDto } from '../types/auth';
import { authService } from '../services/auth/authService';
import { clearCache } from '../utils/cache';

const AUTH_TOKEN_KEY = 'nad_auth_token';
const AUTH_USER_KEY = 'nad_auth_user';

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function persistSession(token: string | null, user: User | null): void {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
  if (user) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(AUTH_USER_KEY);
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  login: (email: string, password?: string) => Promise<boolean>;
  register: (dto: RegisterDto) => Promise<ApiResponse<AuthSession>>;
  logout: () => void;
  hasRole: (roles: RoleType | RoleType[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => readStoredUser());
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(AUTH_TOKEN_KEY));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!savedToken) {
        setIsInitializing(false);
        return;
      }

      // Restore the persisted identity immediately so a page refresh never logs
      // the user out. The session is only cleared on an explicit logout.
      const savedUser = readStoredUser();
      if (savedUser) {
        setUser(savedUser);
        setToken(savedToken);
      }

      try {
        const res = await authService.getCurrentUser(savedToken);
        if (res.success && res.data) {
          // Adopt the server-verified identity only when it matches the stored
          // one (or none was stored) to avoid session/identity flapping.
          if (!savedUser || res.data.user.user_id === savedUser.user_id) {
            setUser(res.data.user);
            setToken(res.data.token);
            persistSession(res.data.token, res.data.user);
          }
        } else if (!savedUser) {
          // A bare token with no persisted identity cannot be trusted.
          persistSession(null, null);
          setToken(null);
          setUser(null);
        }
      } catch (e) {
        // Keep the persisted session alive; only an explicit logout clears it.
        if (!savedUser) {
          persistSession(null, null);
          setToken(null);
          setUser(null);
        }
      }
      setIsInitializing(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password?: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const res = await authService.login({ email, password });
      if (res.success && res.data) {
        setUser(res.data.user);
        setToken(res.data.token);
        persistSession(res.data.token, res.data.user);
        setIsLoading(false);
        return true;
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
    return false;
  };

  const register = async (dto: RegisterDto): Promise<ApiResponse<AuthSession>> => {
    setIsLoading(true);
    try {
      const res = await authService.register(dto);
      if (res.success && res.data) {
        setUser(res.data.user);
        setToken(res.data.token);
        persistSession(res.data.token, res.data.user);
      }
      return res;
    } catch (e: any) {
      return {
        success: false,
        data: null as unknown as AuthSession,
        message: e?.message || 'Registration failed. Please try again.',
        errors: [String(e)],
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (e) {}
    setUser(null);
    setToken(null);
    persistSession(null, null);
    clearCache();
  };

  const hasRole = (roles: RoleType | RoleType[]): boolean => {
    if (!user) return false;
    const roleList = Array.isArray(roles) ? roles : [roles];
    return roleList.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        isInitializing,
        login,
        register,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
